import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { FormikProvider, useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { FormDialog } from "../../../components/common/FormDialog/FormDialog";
import GenericTextField from "../../../components/common/FormFields/GenericTextField";
import GenericCheckboxField from "../../../components/common/FormFields/GenericCheckboxField";
import { useGetVehiclesQuery } from "../../../api/vehiclesApi";
import { useGetTrailersQuery } from "../../../api/trailersApi";
import {
  Detention,
  CreateDetentionDto,
  DetentionLumperStatus,
  DetentionLumperStatusLabel,
  BrokerEmailReminderFrequency,
  BrokerEmailReminderFrequencyLabel,
} from "../../../types/detention";

type DetentionFormValues = Omit<CreateDetentionDto, "amount" | "sendEmailToBroker" | "emailReminderFrequency"> & {
  amount: number | "";
  status?: DetentionLumperStatus;
  sendEmailToBroker: boolean;
  emailReminderFrequency: BrokerEmailReminderFrequency;
};

interface DetentionFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: CreateDetentionDto & { status?: DetentionLumperStatus }, document?: File) => void;
  isLoading: boolean;
  initialValues: Detention | null;
}

const toLocalDatetimeString = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const optionalAmountSchema = Yup.number()
  .transform((value, originalValue) => (originalValue === "" ? undefined : value))
  .min(0, "Must be positive")
  .optional();

const validationSchema = Yup.object({
  loadNumber: Yup.string().required("Load Number is required").max(100),
  brokerEmail: Yup.string().required("Broker Email is required").email("Invalid email").max(200),
  vehicleId: Yup.number().required("Vehicle is required").min(1, "Vehicle is required"),
  trailerId: Yup.number().required("Trailer is required").min(1, "Trailer is required"),
  amount: optionalAmountSchema,
  timeIn: Yup.string().required("Time In is required"),
  timeOut: Yup.string()
    .required("Time Out is required")
    .test("after-time-in", "Time Out must be after Time In", function (val) {
      const { timeIn } = this.parent;
      if (!timeIn || !val) return true;
      return new Date(val) > new Date(timeIn);
    }),
  notes: Yup.string().max(1000).optional(),
  status: Yup.number().oneOf([0, 1, 2]).optional(),
});

const DetentionFormModal: React.FC<DetentionFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  initialValues,
}) => {
  const { data: vehicles = [] } = useGetVehiclesQuery();
  const { data: allTrailers = [] } = useGetTrailersQuery();
  const trailers = allTrailers.filter(
    (t) => !t.isArchived || t.id === (initialValues?.trailerId ?? 0)
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevAmountPopulatedRef = useRef(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const formik = useFormik<DetentionFormValues>({
    initialValues: {
      loadNumber: "",
      brokerEmail: "",
      vehicleId: 0,
      trailerId: 0,
      amount: "",
      timeIn: "",
      timeOut: "",
      notes: "",
      sendEmailToBroker: false,
      emailReminderFrequency: 0,
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      onSubmit(
        {
          loadNumber: values.loadNumber,
          brokerEmail: values.brokerEmail,
          vehicleId: values.vehicleId,
          trailerId: values.trailerId,
          timeIn: values.timeIn,
          timeOut: values.timeOut,
          amount: values.amount !== "" ? Number(values.amount) : undefined,
          notes: values.notes || undefined,
          sendEmailToBroker: values.sendEmailToBroker,
          emailReminderFrequency: values.emailReminderFrequency,
          ...(initialValues && values.status !== undefined ? { status: values.status } : {}),
        },
        selectedFile ?? undefined
      );
    },
  });

  useEffect(() => {
    if (open) {
      if (initialValues) {
        formik.setValues({
          loadNumber: initialValues.loadNumber,
          brokerEmail: initialValues.brokerEmail,
          vehicleId: initialValues.vehicleId,
          trailerId: initialValues.trailerId,
          amount: initialValues.amount ?? "",
          timeIn: toLocalDatetimeString(initialValues.timeIn),
          timeOut: toLocalDatetimeString(initialValues.timeOut),
          notes: initialValues.notes ?? "",
          status: initialValues.status,
          sendEmailToBroker: false,
          emailReminderFrequency: initialValues.emailReminderFrequency ?? 0,
        });
        // Set ref to current state so the sendEmailToBroker effect doesn't fire on open
        prevAmountPopulatedRef.current = initialValues.amount != null;
      } else {
        formik.resetForm();
        setSelectedFile(null);
        prevAmountPopulatedRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, open]);

  const isEdit = !!initialValues;
  const amountPopulated = formik.values.amount !== "" && !Number.isNaN(Number(formik.values.amount));
  // In edit mode, only show the email checkbox if the amount was previously empty (first time setting it)
  const amountWasPreviouslyNull = isEdit && initialValues?.amount == null;
  const showEmailCheckbox = !isEdit || (amountWasPreviouslyNull && amountPopulated);

  useEffect(() => {
    if (!amountPopulated) {
      formik.setFieldValue("sendEmailToBroker", false);
    } else if (!prevAmountPopulatedRef.current) {
      formik.setFieldValue("sendEmailToBroker", true);
    }
    prevAmountPopulatedRef.current = amountPopulated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountPopulated]);

  const handleFormSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(
        Object.keys(errors).reduce((acc: Record<string, boolean>, key) => {
          acc[key] = true;
          return acc;
        }, {}),
        true
      );
      return;
    }
    formik.submitForm();
  };

  const willSendEmail = showEmailCheckbox && amountPopulated && formik.values.sendEmailToBroker;

  return (
    <FormDialog
      open={open}
      title={isEdit ? "Edit Detention" : "Add Detention"}
      onClose={() => { formik.resetForm(); setSelectedFile(null); onClose(); }}
      onSubmit={handleFormSubmit}
      isLoading={isLoading}
      submitLabel={isEdit ? (willSendEmail ? "Save & Send Email" : "Save") : (willSendEmail ? "Create & Send Email" : "Create")}
    >
      <FormikProvider value={formik}>
        <Box sx={{ display: "flex", flexDirection: "column", mt: 1 }}>
          <GenericTextField name="loadNumber" label="Load Number" required size="small" />

          <GenericTextField
            name="brokerEmail"
            label="Broker Email"
            type="email"
            required
            size="small"
          />

          {isEdit && (
            <FormControl fullWidth size="small" required sx={{ mb: 2 }}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                label="Status"
                name="status"
                value={formik.values.status ?? 0}
                onChange={(e) => formik.setFieldValue("status", Number(e.target.value) as DetentionLumperStatus)}
                onBlur={formik.handleBlur}
              >
                {([0, 1, 2] as DetentionLumperStatus[]).map((value) => (
                  <MenuItem key={value} value={value}>
                    {DetentionLumperStatusLabel[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl
            fullWidth
            size="small"
            required
            error={formik.touched.vehicleId && Boolean(formik.errors.vehicleId)}
            sx={{ mb: 2 }}
          >
            <InputLabel id="vehicle-label">Vehicle</InputLabel>
            <Select
              labelId="vehicle-label"
              label="Vehicle"
              name="vehicleId"
              value={formik.values.vehicleId || ""}
              onChange={(e) => formik.setFieldValue("vehicleId", Number(e.target.value))}
              onBlur={formik.handleBlur}
            >
              {vehicles.map((v: any) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.name || v.vehicleGeneral?.unitNumber || `Vehicle #${v.id}`}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.vehicleId && formik.errors.vehicleId && (
              <FormHelperText>{formik.errors.vehicleId}</FormHelperText>
            )}
          </FormControl>

          <FormControl
            fullWidth
            size="small"
            required
            error={formik.touched.trailerId && Boolean(formik.errors.trailerId)}
            sx={{ mb: 2 }}
          >
            <InputLabel id="trailer-label">Trailer</InputLabel>
            <Select
              labelId="trailer-label"
              label="Trailer"
              name="trailerId"
              value={formik.values.trailerId || ""}
              onChange={(e) => formik.setFieldValue("trailerId", Number(e.target.value))}
              onBlur={formik.handleBlur}
            >
              {trailers.map((t: any) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.unitNumber || `Trailer #${t.id}`}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.trailerId && formik.errors.trailerId && (
              <FormHelperText>{formik.errors.trailerId}</FormHelperText>
            )}
          </FormControl>

          <TextField
            required
            fullWidth
            label="Time In"
            name="timeIn"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            size="small"
            value={formik.values.timeIn}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.timeIn && Boolean(formik.errors.timeIn)}
            helperText={formik.touched.timeIn && formik.errors.timeIn}
            sx={{ mb: 2 }}
          />

          <TextField
            required
            fullWidth
            label="Time Out"
            name="timeOut"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            size="small"
            value={formik.values.timeOut}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.timeOut && Boolean(formik.errors.timeOut)}
            helperText={formik.touched.timeOut && formik.errors.timeOut}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Amount ($)"
            name="amount"
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
            size="small"
            value={formik.values.amount}
            onChange={(e) => formik.setFieldValue("amount", e.target.value === "" ? "" : e.target.value)}
            onBlur={formik.handleBlur}
            error={formik.touched.amount && Boolean(formik.errors.amount)}
            helperText={formik.touched.amount && formik.errors.amount}
            sx={{ mb: 1 }}
          />

          {showEmailCheckbox && (
            <GenericCheckboxField
              name="sendEmailToBroker"
              label="Send email to broker"
              disabled={!amountPopulated}
              tooltip={!amountPopulated ? "Enter an amount to enable broker email" : undefined}
            />
          )}

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="email-reminder-frequency-label">Broker Email Reminder Frequency</InputLabel>
            <Select
              labelId="email-reminder-frequency-label"
              label="Broker Email Reminder Frequency"
              name="emailReminderFrequency"
              value={formik.values.emailReminderFrequency}
              onChange={(e) =>
                formik.setFieldValue("emailReminderFrequency", Number(e.target.value) as BrokerEmailReminderFrequency)
              }
              onBlur={formik.handleBlur}
            >
              {([0, 1, 2, 3] as BrokerEmailReminderFrequency[]).map((value) => (
                <MenuItem key={value} value={value}>
                  {BrokerEmailReminderFrequencyLabel[value]}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>How often to resend the reimbursement request email to the broker while pending</FormHelperText>
          </FormControl>

          <GenericTextField name="notes" label="Notes" multiline rows={3} size="small" />

          {!isEdit && (
            <Box sx={{ mb: 2 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                style={{ display: "none" }}
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {selectedFile ? selectedFile.name : "Attach Document (optional)"}
              </Button>
              {selectedFile && formik.values.sendEmailToBroker && (
                <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                  Will be attached to email
                </Typography>
              )}
            </Box>
          )}

          {isEdit && initialValues?.documentFileName && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Current document:{" "}
                <a href={initialValues.documentUrl} target="_blank" rel="noopener noreferrer">
                  {initialValues.documentFileName}
                </a>
              </Typography>
            </Box>
          )}

          {showEmailCheckbox && (
            <Alert
              severity="info"
              icon={<EmailOutlinedIcon fontSize="small" />}
              sx={{ "& .MuiAlert-message": { width: "100%" } }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                Broker notification
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isEdit
                  ? "An amount is being added for the first time. You can choose to email a reimbursement request to the broker."
                  : "When an amount is entered, you can choose to email a reimbursement request to the broker. Any document you attach will be included if email is sent."}
              </Typography>
            </Alert>
          )}
        </Box>
      </FormikProvider>
    </FormDialog>
  );
};

export default DetentionFormModal;
